import { useEffect, useRef, useState } from 'react';

export default function SoundVisualization({ soundGraphics }) {
    const canvasRef = useRef(null);
    const animationFrameRef = useRef(null);
    const [isAtEndPosition, setIsAtEndPosition] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
            const currentScroll = window.scrollY;
            const isEnd = currentScroll >= maxScroll * 0.9;
            setIsAtEndPosition(isEnd);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (!soundGraphics || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        // Set canvas size
        const resizeCanvas = () => {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        };
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        const draw = () => {
            if (!soundGraphics) return;

            // Clear canvas
            ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
            ctx.fillRect(0, 0, width, height);

            // Get frequency data
            const data = soundGraphics.getAllFrequencyData();
            
            // Draw frequency bars
            const barWidth = width / 4;
            const barHeight = height - 60; // More space for labels
            const barSpacing = 5;
            const startX = 40; // Leave space for volume bar

            // Draw volume bar
            ctx.fillStyle = 'white';
            ctx.fillRect(5, 5, 20, (data.volume / 128) * barHeight);
            
            // Draw volume label and value
            ctx.fillStyle = 'white';
            ctx.font = '14px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('Vol', 15, barHeight + 25);
            ctx.font = '12px Arial';
            ctx.fillText(Math.round(data.volume), 15, barHeight + 45);

            // Draw frequency bars with labels
            const ranges = [
              { name: 'Bass', data: data.bass, color: 'red' },
              { name: 'Mid', data: data.mid, color: 'green' },
              { name: 'High', data: data.high, color: 'blue' },
              { name: 'Ultra', data: data.ultra, color: 'yellow' }
            ];

            ranges.forEach((range, index) => {
              const x = startX + index * (barWidth + barSpacing);
              const height = (range.data.average / 255) * barHeight;
              
              // Draw bar
              ctx.fillStyle = range.color;
              ctx.fillRect(x, barHeight - height, barWidth, height);

              // Draw label and value
              ctx.fillStyle = 'white';
              ctx.font = '14px Arial';
              ctx.textAlign = 'center';
              ctx.fillText(range.name, x + barWidth / 2, barHeight + 25);
              ctx.font = '12px Arial';
              ctx.fillText(Math.round(range.data.average), x + barWidth / 2, barHeight + 45);
            });

            // Request next frame
            animationFrameRef.current = requestAnimationFrame(draw);
        };

        // Start animation
        draw();

        // Cleanup
        return () => {
            window.removeEventListener('resize', resizeCanvas);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [soundGraphics]);

    return (
        <div className={`fade-element ${isAtEndPosition ? 'hidden' : ''}`}
            style={{
                position: 'fixed',
                top: '73%',
                right: 18,
                transform: 'translateY(-50%)',
                width: '300px',
                height: '200px',
                background: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '10px',
                overflow: 'hidden',
                zIndex: 2
            }}>
            <canvas
                ref={canvasRef}
                style={{
                    width: '100%',
                    height: '100%',
                    background: 'transparent'
                }}
            />
        </div>
    );
} 